import React, { useState, useEffect } from 'react';
import { dealAPI } from '../services/api';
import UpgradeModal from '../components/UpgradeModal';
import useIsMobile from '../hooks/useIsMobile';

const STATUSES = ['INQUIRY','NEGOTIATING','CONFIRMED','DELIVERED','PAID'];
const PLATFORMS = ['YouTube','Instagram','Both','Other'];
const DEAL_TYPES = ['Sponsored Video','Sponsored Post','Product Review','Affiliate','Brand Ambassador','Other'];

const EMPTY = { brandName:'', contactName:'', contactEmail:'', platform:'YouTube', dealType:'Sponsored Video', amount:'', currency:'INR', status:'INQUIRY', deadline:'', deliverables:'', notes:'' };

export default function DealsPage() {
  const isMobile = useIsMobile();
  const [deals, setDeals]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [filter, setFilter]     = useState('ALL');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [upgrade, setUpgrade]   = useState(null); // { feature, limit }

  const load = () => dealAPI.getAll().then(r => setDeals(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openAdd  = () => { setEditing(null); setForm(EMPTY); setShowForm(true); setError(''); };
  const openEdit = (d) => { setEditing(d); setForm({ ...d, amount: d.amount || '' }); setShowForm(true); setError(''); };
  const closeForm = () => { setShowForm(false); setEditing(null); setError(''); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.brandName.trim()) return setError('Brand name is required');
    setSaving(true); setError('');
    try {
      if (editing) await dealAPI.update(editing.id, form);
      else         await dealAPI.create(form);
      await load(); closeForm();
    } catch (e) {
      const data = e.response?.data;
      if (data?.error === 'PLAN_LIMIT_REACHED') {
        closeForm();
        setUpgrade({ feature: data.feature, limit: data.limit });
      } else {
        setError('Failed to save. Please try again.');
      }
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    await dealAPI.delete(id); load();
  };

  const handleStatusChange = async (id, status) => {
    await dealAPI.updateStatus(id, status); load();
  };

  const filtered = filter === 'ALL' ? deals : deals.filter(d => d.status === filter);
  const totalValue = deals.filter(d => ['CONFIRMED','DELIVERED','PAID'].includes(d.status)).reduce((s, d) => s + (d.amount || 0), 0);

  if (loading) return <div style={{ padding: '2rem', color: 'var(--muted)' }}>Loading deals...</div>;

  if (upgrade) return <UpgradeModal feature={upgrade.feature} limit={upgrade.limit} onClose={() => setUpgrade(null)} />;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="page-title">🤝 Brand Deals</div>
          <div className="page-subtitle">Track every sponsorship from inquiry to payment</div>
        </div>
        <button className="btn btn-green" onClick={openAdd}>+ New Deal</button>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {STATUSES.map(s => {
          const count = deals.filter(d => d.status === s).length;
          return (
            <div key={s} onClick={() => setFilter(filter === s ? 'ALL' : s)}
              className={`badge status-${s}`}
              style={{ cursor: 'pointer', padding: '0.4rem 0.9rem', fontSize: '0.78rem', border: filter === s ? '2px solid currentColor' : '2px solid transparent' }}>
              {s} ({count})
            </div>
          );
        })}
        <div style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '0.9rem', color: 'var(--green)', display: 'flex', alignItems: 'center' }}>
          Confirmed pipeline: ₹{totalValue.toLocaleString('en-IN')}
        </div>
      </div>

      {/* Pipeline view */}
      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🤝</div>
          <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>No deals yet</div>
          <div style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>Add your first brand deal to start tracking</div>
          <button className="btn btn-green" onClick={openAdd}>+ Add First Deal</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(d => (
            <div key={d.id} className="card" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{d.brandName}</div>
                    <span className={`badge status-${d.status}`}>{d.status}</span>
                    {d.platform && <span className="badge badge-gray">{d.platform}</span>}
                    {d.dealType && <span className="badge badge-gray">{d.dealType}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                    {d.amount  && <div style={{ fontSize: '0.85rem' }}><span style={{ color: 'var(--muted)' }}>Amount: </span><strong style={{ color: 'var(--green)' }}>₹{Number(d.amount).toLocaleString('en-IN')}</strong></div>}
                    {d.deadline && <div style={{ fontSize: '0.85rem' }}><span style={{ color: 'var(--muted)' }}>Deadline: </span><strong>{d.deadline}</strong></div>}
                    {d.contactName && <div style={{ fontSize: '0.85rem' }}><span style={{ color: 'var(--muted)' }}>Contact: </span><strong>{d.contactName}</strong></div>}
                  </div>
                  {d.deliverables && <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.4rem' }}>📋 {d.deliverables}</div>}
                  {d.notes && <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.3rem' }}>📝 {d.notes}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                  <select value={d.status} onChange={e => handleStatusChange(d.id, e.target.value)}
                    style={{ padding: '0.35rem 0.65rem', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', background: 'white' }}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(d)} style={{ flex: 1 }}>Edit</button>
                    <button className="btn btn-sm" onClick={() => handleDelete(d.id)} style={{ background: 'var(--red-pale)', color: 'var(--red)', border: 'none' }}>✕</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div onClick={closeForm} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 'var(--r-xl)', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflow: 'auto', padding: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.1rem' }}>{editing ? 'Edit Deal' : '+ New Brand Deal'}</div>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
            </div>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Brand Name *</label>
                  <input className="form-input" value={form.brandName} onChange={e => setForm(f => ({...f, brandName: e.target.value}))} placeholder="e.g. Mamaearth" />
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Amount (₹)</label>
                  <input className="form-input" type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} placeholder="e.g. 15000" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Platform</label>
                  <select className="form-input" value={form.platform} onChange={e => setForm(f => ({...f, platform: e.target.value}))}>
                    {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Deal Type</label>
                  <select className="form-input" value={form.dealType} onChange={e => setForm(f => ({...f, dealType: e.target.value}))}>
                    {DEAL_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Contact Name</label>
                  <input className="form-input" value={form.contactName} onChange={e => setForm(f => ({...f, contactName: e.target.value}))} placeholder="Brand manager name" />
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Contact Email</label>
                  <input className="form-input" type="email" value={form.contactEmail} onChange={e => setForm(f => ({...f, contactEmail: e.target.value}))} placeholder="manager@brand.com" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Status</label>
                  <select className="form-input" value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Deadline</label>
                  <input className="form-input" type="date" value={form.deadline} onChange={e => setForm(f => ({...f, deadline: e.target.value}))} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Deliverables</label>
                <input className="form-input" value={form.deliverables} onChange={e => setForm(f => ({...f, deliverables: e.target.value}))} placeholder="e.g. 1 YouTube video (10 min), 2 Instagram stories" />
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Notes</label>
                <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Any additional notes..." style={{ resize: 'vertical' }} />
              </div>
              {error && <div style={{ padding: '0.65rem', background: 'var(--red-pale)', borderRadius: 'var(--r-md)', fontSize: '0.82rem', color: 'var(--red)' }}>⚠️ {error}</div>}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn btn-green" style={{ flex: 2 }} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Deal'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
